/**
 * ProjectModal Component
 * Modal for creating/editing projects.
 * Template assignment is handled separately via TemplatePickerModal.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import type { ProjectConfig, ProjectType } from '@/lib/types';

// Project type options - labels resolved via t() in component
const PROJECT_TYPE_IDS: Array<{ id: ProjectType; labelKey: string; descKey: string }> = [
  { id: 'web-application', labelKey: 'projectConfig.typeWebApp', descKey: 'projectConfig.typeWebAppDesc' },
  { id: 'mobile-app', labelKey: 'projectConfig.typeMobileApp', descKey: 'projectConfig.typeMobileAppDesc' },
  { id: 'api-backend', labelKey: 'projectConfig.typeApiBackend', descKey: 'projectConfig.typeApiBackendDesc' },
  { id: 'data-platform', labelKey: 'projectConfig.typeDataPlatform', descKey: 'projectConfig.typeDataPlatformDesc' },
  { id: 'infrastructure', labelKey: 'projectConfig.typeInfrastructure', descKey: 'projectConfig.typeInfrastructureDesc' },
  { id: 'other', labelKey: 'projectConfig.typeOther', descKey: 'projectConfig.typeOtherDesc' },
];

interface ProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (project: {
    projectName: string;
    type: ProjectType | null;
    description?: string;
  }) => void;
  editingProject?: ProjectConfig | null;
  isSaving?: boolean;
}

export const ProjectModal: React.FC<ProjectModalProps> = ({
  isOpen,
  onClose,
  onSave,
  editingProject,
  isSaving = false,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [type, setType] = useState<ProjectType | null>(null);
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens/closes or editing project changes
  useEffect(() => {
    if (isOpen) {
      if (editingProject) {
        setName(editingProject.projectName);
        setType(editingProject.type);
        setDescription(editingProject.description || '');
      } else {
        setName('');
        setType(null);
        setDescription('');
      }
      setError(null);
    }
  }, [isOpen, editingProject]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError(t('projectConfig.projectNameRequired'));
      return;
    }

    onSave({
      projectName: name.trim(),
      type,
      description: description.trim() || undefined,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="text-xl font-semibold text-slate-900">
            {editingProject ? t('projectConfig.editProject') : t('projectConfig.addProjectModal')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Error */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Project Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {t('projectConfig.projectName')}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('projectConfig.projectNamePlaceholder')}
              className="w-full px-4 py-2.5 border border-slate-300 rounded-lg
                       focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                       transition-colors"
              autoFocus
            />
          </div>

          {/* Project Type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {t('projectConfig.projectType')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {PROJECT_TYPE_IDS.map((pt) => (
                <button
                  key={pt.id}
                  type="button"
                  onClick={() => setType(type === pt.id ? null : pt.id)}
                  className={`
                    flex flex-col items-start p-3 rounded-lg border-2 text-left
                    transition-all duration-200
                    ${type === pt.id
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-slate-200 hover:border-slate-300'
                    }
                  `}
                >
                  <span className="font-medium text-sm text-slate-900">{t(pt.labelKey)}</span>
                  <span className="text-xs text-slate-500">{t(pt.descKey)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {t('projectConfig.descriptionLabel')}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('projectConfig.descriptionPlaceholder')}
              rows={2}
              className="w-full px-4 py-2.5 border border-slate-300 rounded-lg
                       focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                       transition-colors resize-none"
            />
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200 bg-slate-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-slate-700 hover:bg-slate-200 rounded-lg transition-colors"
            disabled={isSaving}
          >
            {t('projectConfig.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaving || !name.trim()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg
                     hover:bg-indigo-700 transition-colors
                     disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? t('projectConfig.saving') : editingProject ? t('projectConfig.saveChanges') : t('projectConfig.createProject')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;
